import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { useSidebar } from '../../contexts/SidebarContext';
import IprSheetBody from './components/IprSheetBody';
import IprSendMessageModal from './components/IprSendMessageModal';
import { supabase } from '../../lib/supabase';
import {
  buildEmptyIpr,
  computeIprTotal,
  countContactsWithIpr,
  IPR_TOOL_SELECT,
  normalizeIprData,
  toggleToothMarker,
  updateIprContact,
} from '../../lib/iprToothData';
import {
  CASE_SHEET_SELECT,
  mapCaseToSheetPatientData,
} from '../../lib/caseSheetPatientData';
import { isOperatorFamilyRole } from '../../lib/operatorRoles';
import notificationService from '../../services/notificationService';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ── Main Page ─────────────────────────────────────────────────────────────────
const IPRStrippingSheet = () => {
  const [searchParams] = useSearchParams();
  const { isCollapsed, toggleSidebar }  = useSidebar();
  const sheetRef = useRef(null);

  const caseIdParam = searchParams.get('caseId');

  const [caseLoading, setCaseLoading] = useState(Boolean(caseIdParam));
  const [saving,      setSaving]      = useState(false);
  const [pdfLoading,  setPdfLoading]  = useState(false);
  const [sheetId,     setSheetId]     = useState(null);
  const [loadError,   setLoadError]   = useState(null);

  const [patientData,   setPatientData]   = useState(null);
  const [iprData,       setIprData]       = useState(buildEmptyIpr);
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [activeTool,    setActiveTool]    = useState(IPR_TOOL_SELECT);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const breadcrumbItems = [
    { label: 'Dashboard',          path: '/dashboard',           icon: 'LayoutDashboard' },
    { label: 'Conception',         path: '/cases-list',          icon: 'Briefcase' },
    { label: 'Fiche de Stripping', path: '/ipr-stripping-sheet', icon: 'FileText' },
  ];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) return;

        const [{ data: profile }, { data: roleAssignments }] = await Promise.all([
          supabase
            .from('user_profiles')
            .select('id, full_name')
            .eq('id', user.id)
            .maybeSingle(),
          supabase
            .from('user_role_assignments')
            .select('user_roles(role_type)')
            .eq('user_id', user.id)
            .eq('is_active', true),
        ]);

        if (cancelled) return;
        const roleTypes = (roleAssignments || [])
          .map((ra) => ra.user_roles?.role_type)
          .filter(Boolean);

        setCurrentUser({
          id: user.id,
          name: profile?.full_name || user.email || 'Utilisateur',
          roleTypes,
        });
      } catch (err) {
        console.warn('[IPRStrippingSheet] current user:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Load case + IPR sheet ─────────────────────────────────────────────────
  useEffect(() => {
    if (!caseIdParam) {
      setPatientData(null);
      setIprData(buildEmptyIpr());
      setSheetId(null);
      setSelectedTooth(null);
      setActiveTool(IPR_TOOL_SELECT);
      setLoadError(null);
      setCaseLoading(false);
      return;
    }

    const load = async () => {
      setCaseLoading(true);
      setLoadError(null);
      try {
        const { data: caseInfo, error: caseError } = await supabase
          .from('cases')
          .select(CASE_SHEET_SELECT)
          .eq('id', caseIdParam)
          .single();

        if (caseError) throw caseError;

        setPatientData(mapCaseToSheetPatientData(caseInfo));

        const { data: sheet, error: sheetError } = await supabase
          .from('ipr_stripping_sheets')
          .select('id, ipr_data')
          .eq('case_id', caseIdParam)
          .maybeSingle();

        if (sheetError) throw sheetError;

        if (sheet) {
          setSheetId(sheet.id);
          setIprData(normalizeIprData(sheet.ipr_data));
        } else {
          setSheetId(null);
          setIprData(buildEmptyIpr());
        }
      } catch (err) {
        console.error('[IPRStrippingSheet] load error:', err);
        setPatientData(null);
        setSheetId(null);
        setIprData(buildEmptyIpr());
        setLoadError(err.message || 'Cas introuvable');
        toast.error('Erreur lors du chargement : ' + err.message);
      } finally {
        setCaseLoading(false);
      }
    };

    load();
  }, [caseIdParam]);

  // ── IPR update ────────────────────────────────────────────────────────────
  const handleIPRUpdate = useCallback((contactId, value) => {
    setIprData((prev) => updateIprContact(prev, contactId, value));
  }, []);

  const handleToggleMarker = useCallback((toothNumber, markerKey) => {
    setIprData((prev) => toggleToothMarker(prev, toothNumber, markerKey));
  }, []);

  const handleSendCaseMessage = useCallback(
    async (content) => {
      if (!caseIdParam || !currentUser?.id) {
        toast.error('Session utilisateur requise pour envoyer un message.');
        throw new Error('no_user');
      }

      const text = String(content || '').trim();
      if (!text) return;

      const messageText = `[Fiche IPR] ${text}`;
      const caseNumber = patientData?.caseNumber;
      const dentistId = patientData?.dentistId;
      const isDentistAuthor = dentistId && dentistId === currentUser.id;
      const isLabStaff = (currentUser.roleTypes || []).some(
        (r) => r === 'administrator' || isOperatorFamilyRole(r)
      );

      const { error } = await supabase.from('case_messages').insert([
        {
          case_id: caseIdParam,
          sender_id: currentUser.id,
          message_text: messageText,
          message_type: 'comment',
          is_internal: false,
          is_read: false,
        },
      ]);
      if (error) {
        toast.error("Erreur lors de l'envoi du message : " + error.message);
        throw error;
      }

      const link = `/case-detail/${caseIdParam}?tab=messages`;
      const actorId = String(currentUser.id);

      try {
        if (isDentistAuthor) {
          await notificationService.notifyAllAdministrators({
            type: 'info',
            title: 'Nouveau message (fiche IPR)',
            message: `Nouveau message sur le cas ${caseNumber || caseIdParam}`,
            link,
            metadata: {
              caseId: caseIdParam,
              caseNumber,
              senderId: currentUser.id,
              source: 'ipr_sheet',
            },
            excludeUserIds: [actorId],
          });
        } else {
          if (dentistId && dentistId !== currentUser.id) {
            await notificationService.createNotification({
              userId: dentistId,
              type: 'info',
              title: 'Nouveau message (fiche IPR)',
              message: `Vous avez reçu un message sur le cas ${caseNumber || caseIdParam} (fiche IPR)`,
              link,
              metadata: {
                caseId: caseIdParam,
                caseNumber,
                senderId: currentUser.id,
                source: 'ipr_sheet',
              },
            });
          }
          if (isLabStaff) {
            await notificationService.notifyAllAdministrators({
              type: 'info',
              title: 'Nouveau message (fiche IPR)',
              message: `Nouveau message sur le cas ${caseNumber || caseIdParam}`,
              link,
              metadata: {
                caseId: caseIdParam,
                caseNumber,
                senderId: currentUser.id,
                source: 'ipr_sheet',
              },
              excludeUserIds: [actorId, dentistId].filter(Boolean),
            });
          }
        }
      } catch (notifErr) {
        console.warn('[IPRStrippingSheet] notification:', notifErr);
      }

      toast.success('Message envoyé dans la messagerie du dossier.');
    },
    [caseIdParam, currentUser, patientData?.caseNumber, patientData?.dentistId]
  );

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!caseIdParam) { toast.warning("Ouvrez la fiche depuis un dossier."); return; }
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const now   = new Date().toISOString();
      const total = parseFloat(computeIprTotal(iprData));

      if (sheetId) {
        const { error } = await supabase
          .from('ipr_stripping_sheets')
          .update({ ipr_data: iprData, total_ipr_mm: total, updated_by: user?.id || null, updated_at: now })
          .eq('id', sheetId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('ipr_stripping_sheets')
          .insert({ case_id: caseIdParam, ipr_data: iprData, total_ipr_mm: total, created_by: user?.id || null, updated_by: user?.id || null })
          .select('id')
          .single();
        if (error) throw error;
        setSheetId(data.id);
      }
      toast.success('Fiche IPR enregistrée avec succès !');
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement : " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => window.print();

  const handleExportPDF = async () => {
    if (!sheetRef.current) return;
    setPdfLoading(true);
    const el = sheetRef.current;
    el.classList.add('ipr-pdf-export');
    try {
      // Laisser le navigateur appliquer le masquage (toolbar, notes UI)
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        onclone: (doc) => {
          doc.querySelectorAll('.ipr-pdf-hide').forEach((node) => {
            node.style.display = 'none';
          });
        },
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;
      const imgHeight = (canvas.height * usableWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/png');

      let heightLeft = imgHeight;
      let offsetY = margin;

      pdf.addImage(imgData, 'PNG', margin, offsetY, usableWidth, imgHeight);
      heightLeft -= usableHeight;

      while (heightLeft > 1) {
        offsetY = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, offsetY, usableWidth, imgHeight);
        heightLeft -= usableHeight;
      }

      pdf.save(`Fiche-IPR-${patientData?.caseNumber || 'export'}-${Date.now()}.pdf`);
    } catch (err) {
      console.error('[IPRStrippingSheet] PDF export error:', err);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      el.classList.remove('ipr-pdf-export');
      setPdfLoading(false);
    }
  };

  const totalIPR = computeIprTotal(iprData);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

        <div className="main-content">
          <div className="p-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Page header */}
            <div className="flex items-center justify-between mb-6 ipr-sheet-shell px-5 py-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Fiche de Stripping (IPR)</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Réduction inter-proximale — Instructions de traitement
                  {patientData && (
                    <span className="ml-2 inline-flex items-center gap-1 text-primary font-medium">
                      · {patientData.caseNumber}
                      {sheetId && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-normal">
                          Fiche sauvegardée
                        </span>
                      )}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-3 flex-wrap justify-end">
                {caseIdParam && patientData && (
                  <Button
                    variant="outline"
                    iconName="MessageSquare"
                    onClick={() => setMessageModalOpen(true)}
                    disabled={!currentUser?.id || caseLoading}
                  >
                    Envoyer un message
                  </Button>
                )}
                {caseIdParam && patientData && (
                  <Button
                    variant="outline"
                    iconName="Save"
                    onClick={handleSave}
                    loading={saving}
                    disabled={saving || caseLoading}
                  >
                    {saving ? 'Enregistrement...' : sheetId ? 'Mettre à jour' : 'Enregistrer'}
                  </Button>
                )}
                <Button variant="outline" iconName="Printer" onClick={handlePrint} disabled={!patientData}>
                  Imprimer
                </Button>
                <Button
                  variant="default"
                  iconName="Download"
                  onClick={handleExportPDF}
                  loading={pdfLoading}
                  disabled={!patientData || pdfLoading}
                >
                  Exporter PDF
                </Button>
              </div>
            </div>

            {/* ── No caseId in URL ──────────────────────────────────────── */}
            {!caseIdParam && (
              <div className="ipr-sheet-shell border-2 border-dashed border-gray-200 flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                    className="text-primary">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <p className="text-base font-semibold text-gray-700 mb-1">Aucune fiche à afficher</p>
                <p className="text-sm text-gray-400 max-w-sm mb-4">
                  Ouvrez la fiche IPR depuis un dossier (timeline des phases) pour consulter ou modifier les instructions de stripping.
                </p>
                <Button asChild variant="outline" iconName="Briefcase">
                  <Link to="/cases-list">Retour aux dossiers</Link>
                </Button>
              </div>
            )}

            {/* ── Loading ───────────────────────────────────────────────── */}
            {caseIdParam && caseLoading && (
              <div className="ipr-sheet-shell flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-3 text-gray-400">
                  <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  <p className="text-sm">Chargement du cas...</p>
                </div>
              </div>
            )}

            {/* ── Load error ────────────────────────────────────────────── */}
            {caseIdParam && !caseLoading && loadError && (
              <div className="ipr-sheet-shell border-2 border-dashed border-destructive/30 flex flex-col items-center justify-center py-20 text-center">
                <p className="text-base font-semibold text-gray-700 mb-1">Impossible de charger la fiche</p>
                <p className="text-sm text-gray-400 max-w-sm mb-4">{loadError}</p>
                <Button asChild variant="outline" iconName="ArrowLeft">
                  <Link to={`/case-detail/${caseIdParam}`}>Retour au dossier</Link>
                </Button>
              </div>
            )}

            {/* ── IPR Sheet ─────────────────────────────────────────────── */}
            {caseIdParam && !caseLoading && patientData && (
              <>
                {/* Summary stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="ipr-section-surface p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">Total IPR</div>
                    <div className="text-2xl font-bold text-primary">{totalIPR} mm</div>
                  </div>
                  <div className="ipr-section-surface p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">Contacts IPR</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {countContactsWithIpr(iprData)}
                    </div>
                  </div>
                  <div className="ipr-section-surface p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">Patient</div>
                    <div className="text-base font-semibold text-gray-900 truncate">{patientData.patientName}</div>
                  </div>
                  <div className="ipr-section-surface p-4">
                    <div className="text-sm font-medium text-gray-500 mb-1">N° Cas</div>
                    <div className="text-base font-semibold text-gray-900">{patientData.caseNumber}</div>
                  </div>
                </div>

                {/* Printable sheet */}
                <div ref={sheetRef}>
                  <IprSheetBody
                    iprData={iprData}
                    selectedTooth={selectedTooth}
                    onToothSelect={setSelectedTooth}
                    activeTool={activeTool}
                    onToolChange={setActiveTool}
                    onToggleMarker={handleToggleMarker}
                    onIPRUpdate={handleIPRUpdate}
                    patientData={patientData}
                    showPatientHeader
                    showClinicalNotes
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <IprSendMessageModal
        open={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        onSend={handleSendCaseMessage}
        caseNumber={patientData?.caseNumber}
        patientName={patientData?.patientName}
      />

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print\\:shadow-none, .print\\:shadow-none * { visibility: visible; }
          .print\\:shadow-none { position: absolute; left: 0; top: 0; width: 100%; }
          .ipr-pdf-hide, .print\\:hidden { display: none !important; }
        }
        .ipr-pdf-export .ipr-pdf-hide {
          display: none !important;
        }
        .ipr-pdf-export .ipr-contact-selected {
          background: transparent !important;
          box-shadow: none !important;
        }
        .ipr-pdf-export .ipr-dental-arch {
          overflow: visible !important;
        }
        .ipr-pdf-export .ipr-tooth-cell {
          min-width: 2.15rem !important;
        }
        .ipr-pdf-export .ipr-tooth-glyph {
          width: 2rem !important;
          height: 2.5rem !important;
        }
        .ipr-pdf-export .ipr-contact-slot {
          width: 0.5rem !important;
        }
      `}</style>
    </div>
  );
};

export default IPRStrippingSheet;
